import {
  getTopicById,
  getTopicStages,
  type TopicDefinition,
  type TopicStage,
} from "./topic-taxonomy";

export const TOPIC_TAXONOMY_VERSION = "ozk-topic-taxonomy-v1";

/**
 * Runtime capability gate for Topic metadata persistence.
 *
 * Keep this false until the nullable D1 metadata column, repository reads/writes,
 * and preview verification have landed. Draft and URL handoff may still carry
 * Topic context while this is false, but the UI/API must not claim it was saved.
 */
export const TOPIC_METADATA_CAPABILITIES = {
  communityTalkPersistence: false,
} as const;

export type TopicContentIntent = "question" | "experience";

/**
 * Lenient draft/search metadata. Fields are optional because URL handoff and
 * session drafts can be partial or stale.
 */
export type TopicPostMetadata = {
  topicId?: string;
  topicStageId?: string;
  contentIntent?: TopicContentIntent;
  taxonomyVersion?: string;
};

export type TopicPostMetadataInput = {
  topicId?: string | null;
  topicStageId?: string | null;
  contentIntent?: string | null;
  taxonomyVersion?: string | null;
};

/** Canonical shape that may be written to or read from persistent storage. */
export type PersistedTopicPostMetadata = {
  topicId: string;
  topicStageId: string | null;
  contentIntent: TopicContentIntent | null;
  taxonomyVersion: string;
};

export type TopicPostMetadataWriteErrorCode =
  | "INVALID_METADATA"
  | "INVALID_TOPIC"
  | "INVALID_TOPIC_STAGE"
  | "INVALID_CONTENT_INTENT";

export type TopicPostMetadataWriteValidation =
  | { ok: true; value: PersistedTopicPostMetadata | null }
  | {
      ok: false;
      code: TopicPostMetadataWriteErrorCode;
      field?: "topicId" | "topicStageId" | "contentIntent";
    };

export type ResolvedTopicPostMetadata = {
  metadata: TopicPostMetadata;
  topic?: TopicDefinition;
  stage?: TopicStage;
};

export function isTopicContentIntent(
  value: string | null | undefined,
): value is TopicContentIntent {
  return value === "question" || value === "experience";
}

/**
 * Lenient resolver for create URLs, session drafts, and stale client state.
 * Invalid values are removed rather than treated as persistence validation.
 */
export function resolveTopicPostMetadata(
  input: TopicPostMetadataInput,
): ResolvedTopicPostMetadata {
  const topic =
    typeof input.topicId === "string" ? getTopicById(input.topicId) : undefined;
  const stages = topic ? getTopicStages(topic.id) : [];
  const stage =
    topic && typeof input.topicStageId === "string"
      ? stages.find((item) => item.id === input.topicStageId)
      : undefined;
  const contentIntent = isTopicContentIntent(input.contentIntent)
    ? input.contentIntent
    : undefined;

  const metadata: TopicPostMetadata = {};

  if (topic) {
    metadata.topicId = topic.id;
    metadata.taxonomyVersion = TOPIC_TAXONOMY_VERSION;
  }

  if (stage) {
    metadata.topicStageId = stage.id;
  }

  if (contentIntent) {
    metadata.contentIntent = contentIntent;
  }

  return {
    metadata,
    topic,
    stage,
  };
}

/** Explicit name for callers that need lenient draft recovery semantics. */
export function resolveTopicPostMetadataDraft(
  input: TopicPostMetadataInput,
): ResolvedTopicPostMetadata {
  return resolveTopicPostMetadata(input);
}

/**
 * Strict validator for future API create/update writes.
 * A non-null metadata object requires a valid managed Topic. The server always
 * assigns the current taxonomy version instead of trusting the client value.
 */
export function validateTopicPostMetadataWrite(
  input: unknown,
): TopicPostMetadataWriteValidation {
  if (input === null || input === undefined) {
    return { ok: true, value: null };
  }

  const record = asRecord(input);
  if (!record) {
    return { ok: false, code: "INVALID_METADATA" };
  }

  const topicId =
    typeof record.topicId === "string" ? record.topicId.trim() : "";
  const topic = topicId ? getTopicById(topicId) : undefined;
  if (!topic) {
    return { ok: false, code: "INVALID_TOPIC", field: "topicId" };
  }

  let topicStageId: string | null = null;
  if (
    record.topicStageId !== undefined &&
    record.topicStageId !== null &&
    record.topicStageId !== ""
  ) {
    if (typeof record.topicStageId !== "string") {
      return {
        ok: false,
        code: "INVALID_TOPIC_STAGE",
        field: "topicStageId",
      };
    }

    const stage = getTopicStages(topic.id).find(
      (item) => item.id === record.topicStageId,
    );
    if (!stage) {
      return {
        ok: false,
        code: "INVALID_TOPIC_STAGE",
        field: "topicStageId",
      };
    }
    topicStageId = stage.id;
  }

  let contentIntent: TopicContentIntent | null = null;
  if (
    record.contentIntent !== undefined &&
    record.contentIntent !== null &&
    record.contentIntent !== ""
  ) {
    if (
      typeof record.contentIntent !== "string" ||
      !isTopicContentIntent(record.contentIntent)
    ) {
      return {
        ok: false,
        code: "INVALID_CONTENT_INTENT",
        field: "contentIntent",
      };
    }
    contentIntent = record.contentIntent;
  }

  return {
    ok: true,
    value: {
      topicId: topic.id,
      topicStageId,
      contentIntent,
      taxonomyVersion: TOPIC_TAXONOMY_VERSION,
    },
  };
}

/**
 * Safely parses a stored JSON value without throwing or inferring metadata from
 * post text. Invalid or stale relationships are exposed as null to callers.
 */
export function parseStoredTopicPostMetadata(
  value: unknown,
): PersistedTopicPostMetadata | null {
  if (value === null || value === undefined || value === "") return null;

  let parsed: unknown = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }

  const record = asRecord(parsed);
  if (!record) return null;

  const topicId =
    typeof record.topicId === "string" ? record.topicId.trim() : "";
  const topic = topicId ? getTopicById(topicId) : undefined;
  if (!topic) return null;

  let topicStageId: string | null = null;
  if (
    record.topicStageId !== undefined &&
    record.topicStageId !== null &&
    record.topicStageId !== ""
  ) {
    if (typeof record.topicStageId !== "string") return null;
    const stage = getTopicStages(topic.id).find(
      (item) => item.id === record.topicStageId,
    );
    if (!stage) return null;
    topicStageId = stage.id;
  }

  let contentIntent: TopicContentIntent | null = null;
  if (
    record.contentIntent !== undefined &&
    record.contentIntent !== null &&
    record.contentIntent !== ""
  ) {
    if (
      typeof record.contentIntent !== "string" ||
      !isTopicContentIntent(record.contentIntent)
    ) {
      return null;
    }
    contentIntent = record.contentIntent;
  }

  if (
    typeof record.taxonomyVersion !== "string" ||
    record.taxonomyVersion.trim().length === 0
  ) {
    return null;
  }

  return {
    topicId: topic.id,
    topicStageId,
    contentIntent,
    taxonomyVersion: record.taxonomyVersion,
  };
}

export function serializeTopicPostMetadata(
  value: PersistedTopicPostMetadata | null,
): string | null {
  return value === null ? null : JSON.stringify(value);
}

export function parseTopicPostMetadataFromSearch(
  search: string,
): ResolvedTopicPostMetadata {
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);

  return resolveTopicPostMetadata({
    topicId: params.get("topicId"),
    topicStageId: params.get("topicStageId"),
    contentIntent: params.get("intent") ?? params.get("contentIntent"),
  });
}

export function topicPostMetadataToSearch(
  metadata: TopicPostMetadata,
): string {
  const params = new URLSearchParams();

  if (metadata.topicId) {
    params.set("topicId", metadata.topicId);
  }
  if (metadata.topicStageId) {
    params.set("topicStageId", metadata.topicStageId);
  }
  if (metadata.contentIntent) {
    params.set("intent", metadata.contentIntent);
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
